import { useApolloClient, gql, isApolloError } from '@apollo/client';
import type { ApolloClient, DocumentNode, FetchResult, Reference } from '@apollo/client';
import {
    PartialConfigCategory,
    type PartialConfigConnection,
    type PartialConfigDetailQueryVariables,
    type PartialConfigEdge,
    type PartialConfigType,
    type SwarmType,
    type VibrationGeneralPartialConfigCreatePayload,
} from '@/graphql/types';
import { pick, capitalize, camelCase, upperFirst } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { print } from 'graphql';
import { useErrorBoundary } from 'react-error-boundary';
import { SwarmFormController } from '../measuringPoint/form-controller';
import type { SwarmFormControllerConfig } from '../measuringPoint/form-controller';
import { LayoutTree } from '../../reactForm/layout-helper';
import type { FieldValues } from '../../reactForm/types';
import type { TranslatedFormField } from '../measuringPoint/types';
import { schema } from '../../../schema';
import { ConfigHelper } from '../measuringPoint/configHelper';
import { PARTIAL_CONFIG_SUMMARY_FRAGMENT, PARTIAL_CONFIG_DETAIL_QUERY } from './queries';
import { PartialConfigNotFoundError } from './PartialConfigFormErrors';

const { partialConfigLayoutSections } = schema;

type PartialConfigMutationData = Record<string, VibrationGeneralPartialConfigCreatePayload>;

type ShowBoundary = ReturnType<typeof useErrorBoundary>['showBoundary'];

type PartialConfigLayoutSection =
    (typeof partialConfigLayoutSections)[keyof typeof partialConfigLayoutSections];

const partialConfigTypeToLayoutSectionMap = Object.values(PartialConfigCategory).reduce<
    Record<PartialConfigCategory, PartialConfigLayoutSection>
>((acc, category) => {
    const key = category.toLowerCase() as keyof typeof partialConfigLayoutSections;
    const layoutSection = partialConfigLayoutSections[key];

    if (!layoutSection) {
        throw new Error(`Missing layout section mapping for partial config category: ${category}`);
    }

    acc[category] = layoutSection;
    return acc;
}, {} as Record<PartialConfigCategory, PartialConfigLayoutSection>);

function createPartialConfigMutationIdentifiers(
    action: 'create' | 'update',
    swarmType: SwarmType,
    partialConfigType: PartialConfigCategory
) {
    const actionSuffix = capitalize(action);
    const swarmTypeLower = swarmType as string;
    const swarmTypeCap = capitalize(swarmTypeLower);
    const partialTypeCap = upperFirst(camelCase(partialConfigType.toLowerCase()));

    const mutationFieldName = `${swarmTypeLower}${partialTypeCap}PartialConfig${actionSuffix}`;
    const inputTypeName = `${swarmTypeCap}${partialTypeCap}PartialConfig${actionSuffix}Input`;
    const operationName = `${swarmTypeCap}${partialTypeCap}PartialConfig${actionSuffix}`;
    const resultFieldName = 'partialConfig';

    return {
        mutationFieldName,
        inputTypeName,
        operationName,
        resultFieldName,
    } as const;
}

function createMutationDocument({
    mutationFieldName,
    inputTypeName,
    operationName,
    resultFieldName,
}: {
    mutationFieldName: string;
    inputTypeName: string;
    operationName: string;
    resultFieldName: string;
}): DocumentNode {
    const fragmentSource = print(PARTIAL_CONFIG_SUMMARY_FRAGMENT);
    const mutation = `mutation ${operationName}($input: ${inputTypeName}!) {
        ${mutationFieldName}(input: $input) {
            ${resultFieldName} {
                ...PartialConfigSummary
            }
            warnings {
                field
                message
            }
            errors {
                field
                message
            }
        }
    }
    ${fragmentSource}`;

    return gql(mutation);
}

function isPartialConfigConnection(connection: unknown): connection is PartialConfigConnection {
    if (typeof connection !== 'object' || connection === null) {
        return false;
    }

    return (connection as { __typename?: string }).__typename === 'PartialConfigConnection';
}

function isPartialConfigType(value: unknown): value is PartialConfigType {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const candidate = value as Partial<Record<keyof PartialConfigType, unknown>>;

    return (
        candidate.__typename === 'PartialConfigType' &&
        typeof candidate.id === 'string' &&
        typeof candidate.name === 'string' &&
        typeof candidate.swarmType === 'string' &&
        typeof candidate.partialConfigType === 'string'
    );
}

export class PartialConfigController extends SwarmFormController {
    declare partialConfigType: PartialConfigCategory;

    declare graphQLClient: ApolloClient<object>;

    declare mutationDocument: DocumentNode;

    declare mutationFieldName: string;

    declare resultFieldName: keyof VibrationGeneralPartialConfigCreatePayload;

    declare id?: string;

    declare loading$: BehaviorSubject<boolean>;

    declare showBoundary: ShowBoundary;

    static use(
        swarmType: SwarmType,
        isProject: boolean,
        extraConfig: {
            partialConfigType: PartialConfigCategory;
            id?: string;
        }
    ) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const graphQLClient = useApolloClient();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { showBoundary } = useErrorBoundary();

        const { partialConfigType, id } = extraConfig;

        return super.use(swarmType, isProject, {
            partialConfigType,
            graphQLClient,
            id,
            showBoundary,
        }) as unknown as PartialConfigController;
    }

    storeConfig({
        partialConfigType,
        graphQLClient,
        id,
        showBoundary,
        ...config
    }: {
        partialConfigType: PartialConfigCategory;
        graphQLClient: ApolloClient<object>;
        id?: string;
        showBoundary: ShowBoundary;
    } & SwarmFormControllerConfig) {
        this.graphQLClient = graphQLClient;
        this.partialConfigType = partialConfigType;
        this.id = id;
        this.loading$ = new BehaviorSubject(false);
        this.showBoundary = showBoundary;

        const identifiers = createPartialConfigMutationIdentifiers(
            this.id ? 'update' : 'create',
            config.swarmType,
            partialConfigType
        );

        this.mutationDocument = createMutationDocument(identifiers);
        this.mutationFieldName = identifiers.mutationFieldName;
        this.resultFieldName = identifiers.resultFieldName;

        super.storeConfig(config);

        void this.loadExistingConfig();
    }

    private normalizeConfigPayload(configPayload: PartialConfigType['config']): FieldValues {
        if (!configPayload) {
            return {};
        }

        if (typeof configPayload === 'string') {
            try {
                return JSON.parse(configPayload) as FieldValues;
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                throw new Error(`Unable to parse the partial configuration payload: ${message}`);
            }
        }

        if (typeof configPayload === 'object') {
            return configPayload as FieldValues;
        }

        throw new Error(`Unsupported partial configuration payload type: ${typeof configPayload}`);
    }

    async loadExistingConfig() {
        if (!this.id) {
            return;
        }

        this.loading$.next(true);

        try {
            const { data } = await this.graphQLClient.query<
                { partialConfig: PartialConfigType },
                PartialConfigDetailQueryVariables
            >({
                query: PARTIAL_CONFIG_DETAIL_QUERY,
                variables: { id: this.id },
                fetchPolicy: 'network-only',
            });

            const node = data.partialConfig;

            if (!node) {
                this.showBoundary(
                    new PartialConfigNotFoundError(
                        'The requested partial configuration does not exist.'
                    )
                );
                return;
            }

            const configValues = this.normalizeConfigPayload(node.config);
            const convertedConfigValues = this.convertConfigValues(configValues);

            await this.setValues({
                name: node.name,
                ...convertedConfigValues,
            });
        } catch (error) {
            if (error instanceof Error && isApolloError(error)) {
                const isEntityUnknown = error.graphQLErrors.some(
                    (graphQLError) => graphQLError.extensions?.code === 'ENTITY_UNKNOWN'
                );

                if (isEntityUnknown) {
                    this.showBoundary(
                        new PartialConfigNotFoundError(
                            'The requested partial configuration does not exist.'
                        )
                    );
                    return;
                }
            }

            const boundaryError = error instanceof Error ? error : new Error(String(error));
            this.showBoundary(boundaryError);
        } finally {
            this.loading$.next(false);
        }
    }

    createLayoutTree() {
        const originalTree = super.createLayoutTree();

        const configurationNameField: TranslatedFormField = {
            ...this.fields.getByName('name'),
            label: 'Configuration title',
        };

        // Create a new layout tree in which the first group contains all the partial
        // config info, such as the name, etc., and the second group contains the
        // partial section of the larger config.
        return new LayoutTree({
            name: 'root',
            groups: [
                new LayoutTree({
                    name: 'information',
                    title: 'Configuration information',
                    fields: [configurationNameField],
                }),
                // Grab the partial section out of the complete layout.
                originalTree.groups.getByName(
                    partialConfigTypeToLayoutSectionMap[this.partialConfigType]
                ),
            ],
        });
    }

    createDefaultValues() {
        const tree = this.createLayoutTree();
        const fieldNames = tree.getFieldNames();
        const originalDefaults = super.createDefaultValues();
        return pick(originalDefaults, fieldNames);
    }

    async onSubmitFunction(data: FieldValues) {
        this.setSaveIsInProgress(true);

        let response: FetchResult<PartialConfigMutationData> | undefined;

        try {
            const converted = this.convertConfigValues(data, true);
            const inputVariables = this.id ? { ...converted, id: this.id } : converted;

            response = await this.executeMutation(inputVariables);
        } catch (error) {
            this.handleGenericServerError(error);
            return;
        } finally {
            this.setSaveIsInProgress(false);
        }

        const payload = response?.data?.[this.mutationFieldName];

        if (!payload) {
            this.handleGenericServerError('Invalid response from the server.');
            return;
        }

        const mutationErrors = Array.isArray(payload.errors) ? payload.errors : [];

        mutationErrors.forEach((maybeError, index) => {
            if (!maybeError) {
                return;
            }

            const { field, message } = maybeError;
            const normalizedMessage = message ?? undefined;
            if (field) {
                this.rhfProps.setError(field, { type: 'server', message: normalizedMessage });
                return;
            }

            this.setGlobalError(`apiError${index}`, normalizedMessage);
        });

        // Stop here if the server returned validation errors; no success payload expected.
        if (mutationErrors.length > 0) {
            return;
        }

        const resultNode = payload.partialConfig;

        if (!isPartialConfigType(resultNode)) {
            this.handleGenericServerError('Invalid response from the server.');
            return;
        }

        this.onSubmitSuccessful$.next({ id: resultNode.id });
    }

    private executeMutation(
        inputVariables: FieldValues
    ): Promise<FetchResult<PartialConfigMutationData>> {
        return this.graphQLClient.mutate<PartialConfigMutationData>({
            mutation: this.mutationDocument,
            variables: {
                input: inputVariables,
            },
            update: (cache, { data }) => {
                if (this.id) {
                    return;
                }

                const mutationPayload = data?.[this.mutationFieldName];

                if (!mutationPayload || typeof mutationPayload !== 'object') {
                    return;
                }

                const resultNode = mutationPayload[this.resultFieldName];

                if (!isPartialConfigType(resultNode)) {
                    return;
                }

                cache.modify({
                    fields: {
                        partialConfigs(
                            existingConnection: PartialConfigConnection | Reference | undefined,
                            { readField }
                        ): PartialConfigConnection | Reference | undefined {
                            // Only update when this field is the expected connection shape.
                            if (!isPartialConfigConnection(existingConnection)) {
                                return existingConnection;
                            }

                            const alreadyExists = existingConnection.edges.some((edge) => {
                                if (!edge?.node) {
                                    return false;
                                }

                                return readField('id', edge.node) === resultNode.id;
                            });

                            if (alreadyExists) {
                                return existingConnection;
                            }

                            const newEdge: PartialConfigEdge = {
                                __typename: 'PartialConfigEdge',
                                cursor: resultNode.id,
                                node: resultNode,
                            };

                            const totalCount = (existingConnection.totalCount ?? 0) + 1;

                            return {
                                __typename: 'PartialConfigConnection',
                                totalCount,
                                edges: [newEdge, ...existingConnection.edges],
                                pageInfo: existingConnection.pageInfo,
                            };
                        },
                    },
                });
            },
        });
    }

    createConfigHelper(): ConfigHelper {
        // Only general partial configs are config-helper-aware.
        if (this.partialConfigType === PartialConfigCategory.General) {
            return new ConfigHelper(this);
        }

        return super.createConfigHelper();
    }
}
